import { useEffect, useMemo, useState } from "react";
import { usePublicClient } from "wagmi";
import { rwaData } from "../utils/config";
import { getForwardingDepositContracts, KIITESTNET_CHAIN_ID } from "../ethers/contracts";
import { formatUnits, getAddress } from "viem";
import { getSupportedTokens } from "../ethers/tokens";

const useRWAOverview = ({
    chainId,
    userAddress,
    refetch,
}) => {
    const [totalClaimedData, setTotalClaimedData] = useState(null);
    const [tokens, setTokens] = useState([]);
    const publicClient = usePublicClient();

    const forwardingDepositContracts = useMemo(() => getForwardingDepositContracts(chainId), [chainId]);

    const supportedTokens = useMemo(() => getSupportedTokens(chainId), [chainId]);

    useEffect(() => {
        const tokensToAdd = [];
        rwaData.forEach(data => {
            const token = supportedTokens.find(
                (token) => {
                    return token.name === data.defaultToken
                        && token?.isAavePool === (data.tokens?.[data.defaultToken]?.isAave ?? false)
                        && token?.isRWA === true;
                }
            );
            if (token) {
                tokensToAdd.push(token);
            }
        });
        setTokens(tokensToAdd);
    }, [supportedTokens]);

    useEffect(() => {
        if (!forwardingDepositContracts.length || !userAddress || !publicClient) return;
        const fetchLogs = async () => {
            try {
                const isKiiTestnet = chainId === KIITESTNET_CHAIN_ID; // Replace with actual KiiChain testnet ID
                
                const fetchLogsForContract = async (contract) => {
                    const token = supportedTokens.find((token) => token.name === contract.defaultToken);
                    
                    const logParams = {
                        address: getAddress(token.address),
                        event: {
                            type: 'event',
                            name: 'Transfer',
                            inputs: [
                              { name: 'from', type: 'address', indexed: true },
                              { name: 'to', type: 'address', indexed: true },
                              { name: 'value', type: 'uint256', indexed: false },
                            ]
                        },
                        args: {
                            to: getAddress(userAddress),
                            from: getAddress(contract.address),
                        },
                    };
                    
                    if (isKiiTestnet) {
                        // Chunked fetching for KiiChain
                        const currentBlock = await publicClient.getBlockNumber();
                        const maxBlockRange = 10000n;
                        const allChunkLogs = [];
                        let fromBlock = 0n;
                        
                        while (fromBlock <= currentBlock) {
                            const toBlock = fromBlock + maxBlockRange > currentBlock 
                                ? currentBlock 
                                : fromBlock + maxBlockRange;
                            
                            try {
                                const logs = await publicClient.getLogs({
                                    ...logParams,
                                    fromBlock,
                                    toBlock,
                                });
                                allChunkLogs.push(...logs);
                            } catch (chunkError) {
                                console.warn(`Error fetching logs for range ${fromBlock}-${toBlock}:`, chunkError);
                            }
                            
                            fromBlock = toBlock + 1n;
                        }
                        
                        return allChunkLogs;
                    } else {
                        // Standard fetching for other chains
                        return await publicClient.getLogs({
                            ...logParams,
                            fromBlock: 0n,
                            toBlock: 'latest',
                        });
                    }
                };
                
                const allLogs = await Promise.all(
                    forwardingDepositContracts.map(async (contract) => {
                        const logs = await fetchLogsForContract(contract);
                        const token = supportedTokens.find((token) => token.name === contract.defaultToken);
                        
                        return logs.map((log) => ({
                            from: log.args?.from,
                            to: log.args?.to,
                            value: log.args?.value,
                            txHash: log.transactionHash,
                            blockNumber: log.blockNumber,
                            decimals: token?.decimals,
                        }));
                    })
                );

                const formattedLogs = allLogs.flat();

                // Example: You can process the logs further if needed
                const totalValue = formattedLogs.reduce((total, log) => {
                    return total + parseFloat(formatUnits(log.value ?? 0, log.decimals));
                }, 0);

                const parsedValues = totalValue;

                setTotalClaimedData(parsedValues);
            } catch (err) {
                console.error('Error fetching Transfer logs:', err);
            }
        };

        fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userAddress, publicClient, forwardingDepositContracts, tokens]);

    return {
        totalClaimedData,
    };
};

export default useRWAOverview;